import { useContext, useEffect, useRef } from 'react';
import { Text } from '@fluentui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { EditItem } from '@moko/ui';
import { getApiErrorMessage } from '@moko/utils';
import AppContext from '../AppContext';
import {
  errorMessageAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import {
  loadTenantCategories,
  saveTenantCategory,
  type TenantCategory,
} from '../../services/tenantOrderAccessService';
import { tenantCategoriesPath } from './pricingSettings';

export default function TenantCategoryForm() {
  const location = useLocation();
  return <CategoryEditor key={`${location.pathname}${location.search}`} />;
}
function CategoryEditor() {
  const code = new URLSearchParams(useLocation().search).get('code');
  const navigate = useNavigate();
  const context = useContext(AppContext);
  const isSaved = useRef(false);
  const loaded = useRef<TenantCategory | undefined>(undefined);
  const setError = useSetAtom(errorMessageAtom);
  const setSuccess = useSetAtom(successMessageAtom);
  useEffect(() => {
    context.setSelectedTab('Global settings');
  }, []);
  const save = async (
    controller: AbortController,
    item: Record<string, unknown>
  ) => {
    try {
      if (code && !loaded.current)
        throw new Error('Reload the category before saving.');
      const categoryCode =
        code ??
        String(item.code ?? '')
          .trim()
          .toUpperCase();
      const label = String(item.label ?? '').trim();
      if (
        !/^[A-Z][A-Z0-9_-]{0,49}$/.test(categoryCode) ||
        !label ||
        label.length > 100
      )
        throw new Error(
          'Enter a code starting with a letter (up to 50 uppercase letters, digits, underscores or hyphens) and a name up to 100 characters.'
        );
      await saveTenantCategory(controller, categoryCode, {
        label,
        isActive: item.isActive === true || item.isActive === 'true',
        expectedRevision: loaded.current?.revision ?? 0,
      });
      setSuccess(`Category ${label} saved.`);
      void navigate(tenantCategoriesPath);
    } catch (error) {
      setError(getApiErrorMessage(error, 'Unable to save category.'));
      throw error;
    }
  };
  return (
    <div style={{ width: '100%', maxWidth: 640 }}>
      <Text variant="xLarge">{code ? 'Edit category' : 'Add category'}</Text>
      <EditItem
        key={code ?? 'new'}
        itemIdOverride={0}
        callGetBeforeNew={!!code}
        getAction={async (controller) => {
          loaded.current = undefined;
          const category = (await loadTenantCategories(controller)).find(
            (c) => c.code === code
          );
          if (!category)
            throw new Error(
              'Category not found. Return to Categories and refresh.'
            );
          loaded.current = category;
          return category;
        }}
        newAction={save}
        editAction={(controller, _id, item) => save(controller, item)}
        isSaved={isSaved}
        defaultValues={JSON.stringify({ isActive: true })}
        back={tenantCategoriesPath}
        onCancel={() => {
          void navigate(tenantCategoriesPath);
        }}
        metadata={{
          fields: [
            {
              name: 'code',
              fieldType: 'String',
              label: 'Code',
              mandatory: true,
              readonly: !!code,
            },
            {
              name: 'label',
              fieldType: 'String',
              label: 'Name',
              mandatory: true,
            },
            { name: 'isActive', fieldType: 'Checkbox', label: 'Active' },
          ],
          lookups: [],
          interactions: [],
          validations: [
            {
              field: 'code',
              rules: [{ rule: 'NE', errorMessage: 'Code is required.' }],
            },
            {
              field: 'label',
              rules: [{ rule: 'NE', errorMessage: 'Name is required.' }],
            },
          ],
        }}
      />
    </div>
  );
}
